import {
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  Inject,
  TemplateRef,
  Output,
  EventEmitter,
  Input,
  AfterViewInit,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  NgZone,
  DOCUMENT,
} from '@angular/core'
import {
  Observable,
  of,
  BehaviorSubject,
  combineLatest,
  Subject,
  empty,
  Subscription,
} from 'rxjs'
import {TerminalEvent, eventListParams} from '../../interfaces/terminal-event'
import {TerminalEventsDataService} from '../../services/terminal-events-data.service'
import {
  switchMap,
  map,
  tap,
  throttleTime,
  first,
  scan,
  distinctUntilChanged,
  filter,
  startWith,
  pairwise,
  exhaustMap,
  shareReplay,
  delay,
  takeUntil,
  finalize,
  debounceTime,
  take,
} from 'rxjs/operators'
import {
  TermFilter,
  TermsAggregation,
  ApiDocument,
  RangeFilter,
  ElasticFilter,
} from '@app/core/services/data.model'
import {jumpType} from '../../enums/jump-type.enum'

import {ResizedEvent} from '@app/shared/directives/resize-event/resized-event'
import {TerminalEventsListConfigurationService} from '../../services/terminal-events-list-configuration.service'
import {isEqual} from 'lodash'
import {BreakpointObserver, BreakpointState} from '@angular/cdk/layout'
import {NgbModal} from '@ng-bootstrap/ng-bootstrap'
import {DomHelpersService} from '../../services/dom-helpers.service'
import {ListType} from '../../enums/list-type.enum'
import {trigger, transition, style, animate} from '@angular/animations'
import {InfiniteScrollListComponent} from '@app/shared/infinite-scroll-list/infinite-scroll-list.component'
import {ActivatedRoute, QueryParamsHandling, Router} from '@angular/router'
import moment from 'moment'
import {DateFilterEnum} from '@app/core/services/date-filter/date-filter.interface'
import {TransactionReport} from './terminal-events-reports'
import {saveAs} from 'file-saver/FileSaver'

export enum loadDirection {
  TIME_FORWARD = 'time_forward',
  TIME_BACKWARD = 'time_backward',
  TIME_DEFAULT = 'time_default',
}

interface SearchParams {
  filters: TermFilter[]
  query: string
  baseISN: number
  startISN: number
  serialNumber: string
}

export interface EventListState {
  queryParamsHandling: QueryParamsHandling
  queryParams: object | null
  row: TerminalEvent | null | boolean
  replace?: boolean
}

interface ListFilterBlockConfig {
  resetButton: boolean
  lastBootLink: boolean
  clearAllButton: boolean
  eventsFilter: boolean
  dateJumper: boolean
  dynamicFilter: boolean
  textFilter: boolean
}

export interface ListConfig {
  eventName: string
  convenienceFilter: string | null
  textFilterField?: string
  fieldDefinitions: {
    list: string
    detail: string
  }
  enableLastBoot: boolean
  listType: ListType
  filterBlockConfig: ListFilterBlockConfig
  timezoneField?: string
}

export interface DataFetchInterface {
  start: number
  end: number
  query: string
  filters: (TermFilter | RangeFilter)[]
  ingestSequence: number | null
}

@Component({
  selector: 'app-data-list',
  templateUrl: './terminal-events-list.component.html',
  styleUrls: ['./terminal-events-list.component.scss'],
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [
        style({transform: 'translateY(-100%)', opacity: 0}),
        animate('500ms', style({transform: 'translateY(0)', opacity: 1})),
      ]),
      transition(':leave', [
        style({transform: 'translateY(0)', opacity: 1}),
        animate('500ms', style({transform: 'translateY(-100%)', opacity: 0})),
      ]),
    ]),
  ],
  standalone: false,
})
export class TerminalEventsListComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() stateParams$: Observable<eventListParams>
  @Input() selectedRow$: Observable<string>
  @Input() selectedRowData$: Observable<TerminalEvent>
  @Input() resetTrigger$: Observable<boolean>
  @Input() serialNumber$: Observable<string>
  serialNumberAsSubj$ = new BehaviorSubject<string>('')
  @Input() renderingMode: 'events' | 'sync' = 'events'
  @Input() updateFilterOptionsSubject$ = new Subject()
  private searchParamsSubscription: Subscription | undefined

  @Input() listConfig: ListConfig = {
    eventName: 'Events',
    convenienceFilter: 'sourceName',
    fieldDefinitions: {
      list: 'TerminalEventsFieldDefinitionsByType_list.json',
      detail: 'TerminalEventsFieldDefinitionsByType_detail.json',
    },
    enableLastBoot: true,
    listType: ListType.ADVANCED,
    filterBlockConfig: {
      resetButton: true,
      lastBootLink: true,
      clearAllButton: true,
      eventsFilter: true,
      dateJumper: true,
      dynamicFilter: true,
      textFilter: false,
    },
    timezoneField: 'extra-Timezone',
  }
  @Output() onStateChange = new EventEmitter<EventListState>()
  @Output() onResetView = new EventEmitter<boolean>()
  @Output() onSelectedRowChange = new EventEmitter<TerminalEvent | null>()
  @ViewChild(InfiniteScrollListComponent)
  infiniteScrollListComponent: InfiniteScrollListComponent
  @ViewChildren(InfiniteScrollListComponent)
  infiniteScrollListComponents: QueryList<InfiniteScrollListComponent>
  // seee https://stackoverflow.com/questions/34947154/angular-2-viewchild-annotation-returns-undefined
  notDataText = 'No events found for this terminal'
  ListType = ListType

  data$: Observable<Object[]>
  dataSource$: Observable<Object[]>
  dataSubject = new BehaviorSubject('')
  direction = loadDirection.TIME_DEFAULT
  listItemCount = 25
  listRenderTimeout: any = null
  prevTop = 0
  prevBottom = 0
  indexRange = {
    start: null,
    end: null,
    startIngestSequence: null,
    endIngestSequence: null,
  }
  // dataBehaviorSubject$: BehaviorSubject<TerminalEvent[] | null> = new BehaviorSubject([]);
  dynamicFilters$ = new BehaviorSubject({})
  textFilter: string = null
  dateFilter = DateFilterEnum.CUR_DAY
  detailBig = true
  selectedSourceNames$: Observable<{key: string; selected: boolean}[]>
  detailHeight: string
  selectedOffset = 5
  lastOffset: number

  isSmallScreen$: Observable<BreakpointState>
  hasData$: Observable<boolean>

  LoadDirection = loadDirection
  fetching$ = new BehaviorSubject({fetching: false, direction: this.direction})

  autoScrollUp = false
  isTop = true

  protected searchParams$: Observable<SearchParams>
  protected params$: Observable<any>
  protected availableSourceNames$: Observable<any>
  availableSourceNamesSub$ = new BehaviorSubject<any>([])
  previousParams: string | string[]

  hardcodedSyncOpts = [
    {key: 'App', name: 'App'},
    {key: 'Info', name: 'Info'},
    {key: 'Warning', name: 'Warning'},
    {key: 'Error', name: 'Error'},
    {key: 'Trace', name: 'Trace'},
  ]

  private end$ = new Subject<void>()

  constructor(
    private dataService: TerminalEventsDataService,
    public eventListConfig: TerminalEventsListConfigurationService,
    public breakpointObserver: BreakpointObserver,
    private ngModalService: NgbModal,
    private domHelpersService: DomHelpersService,
    public changeDetectorRef: ChangeDetectorRef,
    public zone: NgZone,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) document,
  ) {}
  /**
   * Lifecycle callbacks
   *
   */
  ngOnInit() {
    this.isSmallScreen$ = this.breakpointObserver.observe([
      '(max-width: 767.98px)',
    ])
    this.listItemCount = 200 // Math.floor(2 * (window.innerHeight / 30) * 3 );

    this.hasData$ = this.availableSourceNamesSub$.pipe(
      map((res) => {
        if (res && res.length) {
          return true
        }
        return false
      }),
      shareReplay(),
    )

    // updateFilterOptionsSubject$
    this.selectedSourceNames$ = this.updateFilterOptions()

    if (this.renderingMode === 'events') {
      this.availableSourceNames$ = this.stateParams$.pipe(
        map((stateParams) => {
          // {key: value} object
          return [stateParams.serialNumber, stateParams.params['@timestamp']]
        }),
        // only when serialNumber changes
        distinctUntilChanged(),
        takeUntil(this.end$),
        switchMap(([serialNumber, timestamp]) => {
          const ts = timestamp
            ? timestamp.split('|')
            : [moment().subtract(1, 'day').valueOf(), moment().valueOf()] // takes date from query if doesnt exst default to day - should make ths day a const somewhere
          const minTs = ts[0]
          const maxTs = ts[1]

          return this.dataService.retrieveAggregations(
            this.dataService.createAggregationsQuery(
              [
                new TermsAggregation(
                  this.listConfig.convenienceFilter,
                  this.listConfig.convenienceFilter,
                  25,
                ),
              ],
              [
                new TermFilter('serialNumber', serialNumber),
                new RangeFilter(minTs, maxTs),
              ],
              this.listConfig.eventName,
            ),
            this.listConfig.convenienceFilter,
          )
        }),
        shareReplay(),
      )

      this.availableSourceNames$.subscribe((e) => {
        this.availableSourceNamesSub$.next(e)
      })
    }

    this.searchParams$ = this.stateParams$.pipe(
      // this is for pairwise because it needs a first value
      startWith(null),
      pairwise(),
      filter(([oldParams, newParams]) => {
        // on load in most cases the first is null (falsy) -> return true to query for the first time
        if (!oldParams) {
          return true
        }
        // this is an important safeguard against an infinite change
        if (isEqual(oldParams, newParams)) {
          return false
        }
        // if baseISN is the same, this must have been triggered by another change
        if (newParams.params.baseISN === oldParams.params.baseISN) {
          return true
        }
        // if only the baseISN changed (and startISN maybe) no new query is needed, else yes query please!
        // check whether the row is visible
        return !this.domHelpersService.checkRowVisible(newParams.params.baseISN)
      }),
      map((res) => {
        const result = this.handleSearchParams(res)
        if (result.clearDynamicFilters) {
          this.dynamicFilters$.next({})
        }
        return result.queryObject
      }),
    )
    // @ts-ignore
    this.dataSource$ = this.serialNumber$.pipe(
      switchMap((serialNumber) => {
        this.serialNumberAsSubj$.next(serialNumber)
        return this.searchParams$.pipe(
          filter((params) => {
            // find the right event and reload list with event selected
            const initEventId = params.filters.find(
              (flt) => flt.field === 'initEventId',
            )
            // initEventId can be passed to load an event. This shortcircuits the initializattion and redirects (hence the filter)
            if (initEventId) {
              this.handleInitEvent(initEventId.matches, params)
              return false
            }
            return true
          }),
          tap(() => {
            // close modals because searchParams changed
            this.ngModalService.dismissAll()
            // dirty but assign the type to not be scroll type
            this.dataSubject.next(null)
            this.direction = loadDirection.TIME_DEFAULT
            // reset indexRange on new filter
            this.indexRange = {
              start: null,
              end: null,
              startIngestSequence: null,
              endIngestSequence: null,
            }
          }),
          switchMap((query) => {
            let eventType = null
            let lastOffset = 0
            let addedCount = 0
            let removedCount = 0
            let listCount = 0 // wait 200 ms and check whether the scrollpostion is on top
            return this.dataSubject.pipe(
              throttleTime(500),
              map((type) => {
                eventType = type
                return query
              }),
              debounceTime(100), // this fixes a cancelled request in the network tab.
              exhaustMap((searchParams) => {
                let start = 0
                let end = this.listItemCount * 2
                if (this.direction !== loadDirection.TIME_DEFAULT) {
                  start =
                    this.direction === loadDirection.TIME_BACKWARD
                      ? this.indexRange.end + 1
                      : this.indexRange.start - this.listItemCount
                  end =
                    this.direction === loadDirection.TIME_BACKWARD
                      ? this.indexRange.end + 1 + this.listItemCount
                      : this.indexRange.start
                }
                // if no ingestSequence is passed, it should be default (0 to listitemcount)
                if (!searchParams.startISN && (start < 0 || end < 0)) {
                  if (this.autoScrollUp) {
                    // load more up should reset the view if nothing else is there
                    this.resetView()
                  }
                  return []
                }
                return this.getData({
                  start: start,
                  end: end,
                  filters: searchParams.filters,
                  query: searchParams.query,
                  ingestSequence: searchParams.startISN,
                })
              }),
              tap((res) => {
                if (res.buckets) {
                  if (this.renderingMode === 'sync') {
                    this.availableSourceNamesSub$.next(this.hardcodedSyncOpts)
                  }
                  if (this.renderingMode === 'events') {
                    // load dynamically
                  }
                }
                // set lastOffsetSize as late as possible
                if (this.infiniteScrollListComponent) {
                  lastOffset =
                    this.infiniteScrollListComponent.viewport.measureScrollOffset(
                      'top',
                    )
                }
                listCount = res.count
              }),
              map((res: {items: ApiDocument[]}) => {
                return res.items
              }),
              scan<ApiDocument[]>((all, current) => {
                if (this.direction === loadDirection.TIME_FORWARD) {
                  addedCount = current.length
                  if ((all as ApiDocument[]).length >= 2 * this.listItemCount) {
                    return [
                      ...current,
                      ...(all as ApiDocument[]).slice(0, this.listItemCount),
                    ]
                  }
                  return [...current, ...(all as ApiDocument[])]
                }
                if ((all as ApiDocument[]).length >= 3 * this.listItemCount) {
                  removedCount = this.listItemCount
                  return [
                    ...(all as ApiDocument[]).splice(this.listItemCount),
                    ...current,
                  ]
                }
                return [...(all as ApiDocument[]), ...current]
              }),
              tap((res) => {
                if (!(res as any[]).length) {
                  return
                }

                let topOffset = 0
                if (
                  this.infiniteScrollListComponent &&
                  this.infiniteScrollListComponent.viewport
                ) {
                  topOffset =
                    this.infiniteScrollListComponent.viewport.measureScrollOffset(
                      'top',
                    )
                }

                // if end list, don't jump back!
                if (
                  !Array.isArray(res) ||
                  !(res[res.length - 1].scrollIndex === listCount - 1)
                ) {
                  if (this.direction === loadDirection.TIME_FORWARD) {
                    const toScrollTo = topOffset + addedCount * 30
                    this.scrollTo(toScrollTo)
                    if (this.autoScrollUp) {
                      setTimeout(() => {
                        let toScrollSmooth = addedCount
                        if (addedCount > 5) {
                          toScrollSmooth = 5
                        }
                        if (addedCount > 0) {
                          this.infiniteScrollListComponent.viewport.scrollToOffset(
                            toScrollTo - toScrollSmooth * 30,
                            'smooth',
                          )
                        }
                      })
                    }
                  } else if (this.direction === loadDirection.TIME_BACKWARD) {
                    this.scrollTo(topOffset - removedCount * 30)
                  } else if (this.direction === loadDirection.TIME_DEFAULT) {
                    if (
                      this.infiniteScrollListComponent &&
                      this.infiniteScrollListComponent.viewport.measureScrollOffset(
                        'top',
                      ) === 0
                    ) {
                      return
                    }
                    this.scrollToIndex(0)
                  }
                }
                // update indexRange after scroll so previous values can be used to calculate scrollToIndex
                this.indexRange = {
                  start: res[0].scrollIndex,
                  end:
                    (res as any[]).length > 0
                      ? res[(res as any[]).length - 1].scrollIndex
                      : 0,
                  startIngestSequence: res[0].ingestSequence,
                  endIngestSequence:
                    (res as any[]).length > 0
                      ? res[(res as any[]).length - 1].ingestSequence
                      : 0,
                }
              }),
              tap((data: any[]) => {
                if (!data.length) {
                  return
                }
                // check whether the selected row is still part of the rows in the viewport
                // usecase: suppose you have filtered on boot events, and are stepping through them. Then you widen your filter, you still want the selected boot event visible in your viewport.
                // if not, do a jumpToRow method call

                // get selected row
                if (eventType === 'scroll') {
                  return
                }
                if (this.listRenderTimeout !== null) {
                  clearTimeout(this.listRenderTimeout)
                  this.listRenderTimeout = null
                }
                this.listRenderTimeout = setTimeout(() => {
                  this.selectedRowData$
                    .pipe(
                      first(),
                      tap((selectedRow) => {
                        let jumpToRow = true
                        if (selectedRow && !this.autoScrollUp) {
                          jumpToRow = !this.domHelpersService.checkRowVisible(
                            selectedRow.ingestSequence.toString(),
                          )
                        } else {
                          return
                        }
                        if (jumpToRow && !this.autoScrollUp) {
                          this.searchParams$
                            .pipe(
                              first(),
                              tap((params) => {
                                this.jumpToRow(selectedRow, params)
                              }),
                            )
                            .subscribe()
                        }
                      }),
                    )
                    .subscribe()
                })
              }),
              finalize(() => {
                this.autoScrollUp = false
              }),
            )
          }),
        )
      }),
      shareReplay(),
    )

    this.data$ = combineLatest(this.dataSource$, this.selectedRow$).pipe(
      map(([dataSource, selectedRow]) => {
        // max amount of dynamicColumns
        let dynamicColumnsCount = 0
        const complementedData = dataSource.map((item: TerminalEvent) => {
          item.dynamicColumns = []
          if (parseInt(selectedRow) === item.ingestSequence) {
            item.active = true
            // set selected item in the store
            this.setState({
              queryParamsHandling: 'merge',
              queryParams: {},
              row: item,
            })
          } else {
            item.active = false
          }
          // check config for row
          Object.assign(
            item,
            this.eventListConfig.getListViewConfig(
              item,
              this.listConfig.fieldDefinitions.list,
              this.listConfig.convenienceFilter,
            ),
          )
          if (item.dynamicColumns.length > dynamicColumnsCount) {
            dynamicColumnsCount = item.dynamicColumns.length
          }
          return item
        })
        if (!dynamicColumnsCount) {
          return complementedData
        }
        return complementedData.map((item: TerminalEvent) => {
          // last dynamic item must have colspan
          if (item.dynamicColumns.length < dynamicColumnsCount) {
            if (item.dynamicColumns.length === 0) {
              item.dynamicColumns.push({
                value: '',
                colspan: dynamicColumnsCount,
                sourceName: null,
                sourceField: null,
              })
            } else {
              if (item.dynamicColumns.length !== dynamicColumnsCount) {
                item.dynamicColumns[item.dynamicColumns.length - 1].colspan =
                  dynamicColumnsCount - item.dynamicColumns.length + 1
              }
            }
          }
          return item
        })
      }),
      shareReplay(),
    )
  }

  updateFilterOptions() {
    return this.availableSourceNamesSub$.pipe(
      switchMap((sourceNames) => {
        if (this.previousParams && this.previousParams.length) {
          this.assignMissingQueries(sourceNames, this.previousParams)
        }
        return this.stateParams$.pipe(
          map((stateParams) => {
            return sourceNames.map((item) => {
              item.selected = false
              if (
                stateParams.params.hasOwnProperty(
                  this.listConfig.convenienceFilter,
                ) &&
                stateParams.params[this.listConfig.convenienceFilter].indexOf(
                  item.key,
                ) !== -1
              ) {
                item.selected = true
              }
              return item
            })
          }),
        )
      }),
      shareReplay(),
    )
  }

  assignMissingQueries(filters, previous: string[] | string) {
    const previousFilters = filters
      .filter((item) => previous.includes(item.key))
      .map((item) => item.key)
    const queryParams = {}
    if (previousFilters.length) {
      queryParams[this.listConfig.convenienceFilter] = previousFilters
      this.previousParams = null
      return this.setState({
        queryParamsHandling: 'merge',
        queryParams: queryParams,
        row: false,
      })
    }
  }

  ngAfterViewInit(): void {
    this.infiniteScrollListComponents.changes
      .pipe(
        delay(0),
        takeUntil(this.end$),
        switchMap((comps: QueryList<InfiniteScrollListComponent>) => {
          if (comps.first) {
            return comps.first.scrollTopPosition$.pipe(
              map((position) => position === 0),
            )
          }
          return empty()
        }),
        distinctUntilChanged(),
        tap((r) => {
          // weird .. probably because cdk scolling events run outside ngZone
          this.zone.run(() => {
            this.isTop = r
          })
        }),
      )
      .subscribe()
  }

  ngOnDestroy() {
    if (this.listRenderTimeout !== null) {
      clearTimeout(this.listRenderTimeout)
    }
    this.end$.next()
    this.end$.complete()
  }

  handleInitEvent(eventId, params) {
    const filters = [new TermFilter('id', eventId)]
    this.dataService
      .retrieveData(
        this.dataService.createQuery(
          {count: 1, filters: filters},
          this.listConfig.eventName,
        ),
        [{field: 'ingestSequence', asc: false}],
      )
      .pipe(
        first(),
        map((res) => res.items),
        tap((rows) => {
          // remove initEventId from filters
          params.filters = params.filters.filter((item) => {
            return item.field !== 'initEventId'
          })
          this.jumpToRow(
            rows[0],
            {filters: params.filters, query: ''},
            true,
            true,
          )
        }),
      )
      .subscribe()
  }

  open(detailViewModal: TemplateRef<any>) {
    // modal only on small screen
    this.isSmallScreen$
      .pipe(
        first(),
        tap((res) => {
          if (res.matches) {
            this.ngModalService
              .open(detailViewModal, {
                ariaLabelledBy: 'modal-detail-view',
                windowClass: 'modal-holder',
                centered: true,
              })
              .result.then(
                (result) => {},
                (reason) => {},
              )
          }
        }),
      )
      .subscribe()
  }

  /**
   * Route change handlers
   *
   */
  handleSearchParams(res): {clearDynamicFilters: boolean; queryObject: any} {
    const results = res[1]
    const filters: ElasticFilter[] = []
    const queries: string[] = []
    const f = Object.assign(
      {},
      {serialNumber: results.serialNumber},
      results.params,
    )
    let baseISN: number | null = null
    let startISN: number | null = null
    let clearDynamicFilters = true
    Object.keys(f).forEach((key) => {
      // baseISN = base ingestSequenceNumber (the ingestsequence number to start with, the selected index for the list)
      // this is not a filter but ultimatly an offset
      if (key === 'baseISN') {
        baseISN = f[key]
        return
      }
      // startISN = start ingestSequenceNumber (the ingestsequence number to start with, the zero index for the list)
      // this is not a filter but ultimatly an offset
      if (key === 'startISN') {
        startISN = f[key]
        return
      }

      // update dynamicFilters$ Observable and add them to the query
      if (key === 'dynamicFilter') {
        clearDynamicFilters = false
        const r = {}
        const resultSet = {}
        if (Array.isArray(f[key])) {
          f[key].forEach((item) => {
            const v = item.split('|')
            if (!resultSet.hasOwnProperty(v[0])) {
              resultSet[v[0]] = v[1]
            } else {
              if (Array.isArray(resultSet[v[0]])) {
                resultSet[v[0]] = [...resultSet[v[0]], v[1]]
              } else {
                resultSet[v[0]] = [resultSet[v[0]], v[1]]
              }
            }
            r[item.replace('|', ': ')] = v[1]
          })
        } else {
          const v = f[key].split('|')
          r[f[key].replace('|', ': ')] = v[1]
          resultSet[v[0]] = v[1]
        }
        this.dynamicFilters$.next(r)
        // add query or queries
        // return;
        Object.keys(resultSet).forEach((key) => {
          if (Array.isArray(resultSet[key])) {
            // escape chars with JSON.stringify (it seems the value between double quotes does the trick for elasic queries)
            queries.push(
              ` +(${resultSet[key].map((item) => ` +(${key.replace(/ /g, '\\ ')}:${item.replace(/ /g, '\\ ')} `).join(' ')}) `,
            )
          } else {
            queries.push(
              ` +(${key.replace(/ /g, '\\ ')}:${resultSet[key].replace(/ /g, '\\ ')}) `,
            )
          }
        })
        return
      }

      if (!f[key] || !f[key].length) {
        return
      }
      if (Array.isArray(f[key])) {
        // If is array, it should be 'or' query, utilizing the lucene query language for this.
        const q = []
        f[key].forEach((item) => {
          q.push(`${key.replace(/ /g, '\\ ')}:${JSON.stringify(item)}`)
        })
        if (q.length) {
          queries.push(` +(${q.join(' ')}) `)
        }
      } else if (key == '@timestamp') {
        //another hardcoded fix
        const [from, to] = f[key].split('|')
        filters.push(new RangeFilter(from, to))
      } else {
        filters.push(new TermFilter(key, f[key]))
      }
    })

    return {
      clearDynamicFilters: clearDynamicFilters,
      queryObject: {
        filters: filters,
        query: queries.join(' AND '),
        baseISN: baseISN,
        startISN: startISN,
        serialNumber: results.serialNumber,
      },
    }
  }

  getStartAndEndOfDay(dateString: string): {start: number; end: number} {
    const date = new Date(dateString)

    const start = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    ).getTime()
    const end = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate() + 1,
      0,
      0,
      -1,
    ).getTime()

    return {start, end}
  }

  /**
   * Event handlers
   *
   */

  forceLoadUp() {
    if (
      this.infiniteScrollListComponent.viewport.measureScrollOffset('top') === 0
    ) {
      this.data$
        .pipe(
          first(),
          tap((res: ApiDocument[]) => {
            this.handleScrollEnd({direction: 'up', autoScroll: true})
          }),
        )
        .subscribe()
    }
  }

  handleScrollEnd({direction, autoScroll = false}) {
    if (direction === 'up') {
      // this.direction = loadDirection.TIME_FORWARD;
    } else {
      this.direction = loadDirection.TIME_BACKWARD
    }
    if (autoScroll) {
      this.direction = loadDirection.TIME_FORWARD
      this.autoScrollUp = true
    }
    this.dataSubject.next('scroll')
  }

  onResized(event: ResizedEvent) {
    this.detailHeight = event.newHeight + 'px'
  }

  setDetailViewSize(event) {
    if (event.detailBig) {
      this.detailBig = true
      // set big
    } else {
      // set small
      this.detailBig = false
    }
  }

  /**
   * Helpers
   *
   */

  scrollToIndex(index, type = undefined) {
    if (
      typeof this.infiniteScrollListComponent !== 'undefined' &&
      typeof this.infiniteScrollListComponent.viewport !== 'undefined'
    ) {
      this.infiniteScrollListComponent.viewport.scrollToIndex(index, type)
    }
  }

  scrollTo(offset) {
    if (
      typeof this.infiniteScrollListComponent !== 'undefined' &&
      typeof this.infiniteScrollListComponent.viewport !== 'undefined'
    ) {
      this.infiniteScrollListComponent.viewport.scrollTo({
        top: Math.abs(offset),
      })
    }
  }

  /**
   * Filter actions
   *
   */
  dynamicFilterChanged(res) {
    this.stateParams$
      .pipe(
        first(),
        tap((stateParams) => {
          let dynamicFilter = []
          // first remove key by name
          if (res.setSelected) {
            if (stateParams.params.hasOwnProperty('dynamicFilter')) {
              dynamicFilter = [
                ...(Array.isArray(stateParams.params.dynamicFilter)
                  ? stateParams.params.dynamicFilter
                  : [stateParams.params.dynamicFilter]),
                ...[res.filter.key + '|' + res.filter.value],
              ].filter(String)
            } else {
              dynamicFilter = [res.filter.key + '|' + res.filter.value]
            }
          } else {
            // remove
            if (stateParams.params.hasOwnProperty('dynamicFilter')) {
              if (Array.isArray(stateParams.params.dynamicFilter)) {
                dynamicFilter = stateParams.params.dynamicFilter.filter(
                  (item) => {
                    return item !== res.filter.key + '|' + res.filter.value
                  },
                )
              }
            } else {
              dynamicFilter = null
            }
          }
          return this.setState({
            queryParamsHandling: 'merge',
            queryParams: {
              baseISN: res.ingestSequence,
              dynamicFilter: dynamicFilter,
            },
            row: false,
          })
        }),
      )
      .subscribe()
  }

  removeDynamicFilter(event: {key: string}) {
    const v = event.key.split(': ')
    this.stateParams$
      .pipe(
        first(),
        tap((stateParams) => {
          this.dynamicFilterChanged({
            filter: {key: v[0], value: v[1]},
            ingestSequence: stateParams.params.hasOwnProperty('baseISN')
              ? stateParams.params.baseISN
              : null,
            setSelected: false,
          })
        }),
      )
      .subscribe()
  }

  selectAllFilters(event) {
    this.availableSourceNamesSub$
      .pipe(
        first(),
        tap((sourceNames) => {
          const queryParams = {}
          // if any are unselected we select all
          const allSelected =
            sourceNames.find((sn) => !sn.selected) === undefined
          queryParams[this.listConfig.convenienceFilter] = !allSelected
            ? sourceNames.map((item) => item.key)
            : []
          this.setState({
            queryParamsHandling: 'merge',
            queryParams: queryParams,
            row: false,
          })
        }),
      )
      .subscribe()
  }

  onFilterChanged(item) {
    this.stateParams$
      .pipe(
        first(),
        tap((stateParams) => {
          let queryParam = ''
          let queryParams = []
          if (item.selected) {
            // add to filters
            if (
              !stateParams.params.hasOwnProperty(
                this.listConfig.convenienceFilter,
              )
            ) {
              queryParam = item.key
            } else if (
              typeof stateParams.params[this.listConfig.convenienceFilter] ===
              'string'
            ) {
              if (
                stateParams.params[this.listConfig.convenienceFilter] !==
                item.key
              ) {
                queryParams = [
                  stateParams.params[this.listConfig.convenienceFilter],
                  item.key,
                ]
              }
            } else {
              if (
                stateParams.params[this.listConfig.convenienceFilter].indexOf(
                  item.key,
                ) === -1
              ) {
                queryParams = [
                  ...stateParams.params[this.listConfig.convenienceFilter],
                  item.key,
                ]
              }
            }
          } else {
            // remove from filters
            if (
              !stateParams.params.hasOwnProperty(
                this.listConfig.convenienceFilter,
              )
            ) {
              // do nothing
            } else if (
              Array.isArray(
                stateParams.params[this.listConfig.convenienceFilter],
              )
            ) {
              if (
                stateParams.params[this.listConfig.convenienceFilter].indexOf(
                  item.key,
                ) !== -1
              ) {
                queryParams = stateParams.params[
                  this.listConfig.convenienceFilter
                ].filter((it) => {
                  return it !== item.key && it !== ''
                })
              }
            }
          }
          const qPObject = {}
          qPObject[this.listConfig.convenienceFilter] = queryParams.length
            ? queryParams
            : queryParam
          return this.setState({
            queryParamsHandling: 'merge',
            queryParams: qPObject,
            row: false,
          })
        }),
      )
      .subscribe()
  }

  private firstDateSwitch = true
  setDateRange(range) {
    //ok we are hacking out the filterdate initial set here because it is used everywhere
    if (this.firstDateSwitch) {
      this.firstDateSwitch = false
      return
    }
    this.stateParams$
      .pipe(
        first(),
        tap((stateParams) => {
          this.previousParams =
            stateParams.params[this.listConfig.convenienceFilter]
          const queryParams = {}
          //set the date range in the timestamp

          queryParams['@timestamp'] = range.start + '|' + range.end
          return this.setState({
            queryParamsHandling: '',
            queryParams: queryParams,
            row: false,
          })
        }),
      )
      .subscribe()
  }

  filterText() {
    this.stateParams$
      .pipe(
        first(),
        tap((stateParams) => {
          const queryParams = {}
          //this is a massive hack, but this component is pretty broken anyway... this would override any existing dynamic filters
          //however we don't use them together
          queryParams['dynamicFilter'] = [
            this.listConfig.textFilterField + '|*' + this.textFilter + '*',
          ]
          return this.setState({
            queryParamsHandling: 'merge',
            queryParams: queryParams,
            row: false,
          })
        }),
      )
      .subscribe()
  }

  clearAllFilters() {
    return this.setState({
      queryParamsHandling: null,
      queryParams: null,
      row: null,
    })
  }

  // set state, pass a copy of selected row data
  // row false just doesn't change the current selected row
  setState({
    queryParamsHandling = 'merge',
    queryParams = {},
    row = null,
    replace = false,
  }: EventListState) {
    // initEventId is never set from setState, it is init logic
    if (queryParams) {
      queryParams['initEventId'] = null
    }
    this.onStateChange.emit({
      queryParamsHandling: queryParamsHandling,
      queryParams: queryParams,
      row: row,
      replace: replace,
    })
  }

  /**
   * Row Jumpers and selectors
   *
   */
  jumpToLastBoot() {
    // remove all filters (because BOOT may be filtered)
    // just get the event id and query for the most recent boot event
    this.stateParams$
      .pipe(
        first(),
        map((stateParams) => {
          const extendedParam = Object.assign(
            {serialNumber: stateParams.serialNumber},
            stateParams.params,
          )
          return extendedParam
        }),
        tap((params) => {
          const filters = [new TermFilter('serialNumber', params.serialNumber)]
          this.dataService
            .retrieveData(
              this.dataService.createQuery(
                {
                  count: 1,
                  filters: [
                    ...filters,
                    ...[new TermFilter('sourceName', 'BOOT')],
                  ],
                },
                this.listConfig.eventName,
              ),
              [{field: 'ingestSequence', asc: false}],
            )
            .pipe(
              first(),
              map((res) => res.items),
              tap((rows) => {
                this.jumpToRow(rows[0], {filters: filters}, false)
              }),
            )
            .subscribe()
        }),
      )
      .subscribe()
  }

  jumpTo(event) {
    // Assign range to a local variable because the indexRange may be reset after an api call
    const cachedIndexRange = this.indexRange
    let sourceName = event.type
    const direction = event.direction
    let base = 0
    // get searchparams to replay current query (filters)
    combineLatest(this.searchParams$, this.selectedRowData$)
      .pipe(
        first(),
        // it's one big side-effect because it ultimately redirects
        tap((result) => {
          const params = result[0]
          // get the next of sourceName type in the right direction
          let mustOffset = true
          base = params.baseISN
            ? params.baseISN
            : cachedIndexRange.startIngestSequence
          if (result[1]) {
            base = result[1].ingestSequence
          }
          if (event.type === jumpType.FOCUS) {
            this.jumpToRow(result[1], result[0], true)
            return
          }
          sourceName = 'BOOT'
          if (event.type === jumpType.SAME_TYPE) {
            if (result[1]) {
              sourceName = result[1][this.listConfig.convenienceFilter]
            }
          }
          if (event.type === jumpType.ANY_TYPE) {
            sourceName = null
          }
          // Selected row is not boot but next boot is requested. So no offset, just find the first boot
          if (result[1] && event.type === jumpType.BOOT) {
            if (
              result[1][this.listConfig.convenienceFilter] !== jumpType.BOOT
            ) {
              mustOffset = false
            }
          }
          // mustOffset if starting from the same type else it must be the first in the range
          this.jumpToNextOrPreviousRow(
            sourceName,
            direction,
            base,
            params,
            mustOffset,
          )
        }),
      )
      .subscribe()
  }

  jumpToDatetime({timestamp}) {
    this.searchParams$
      .pipe(
        first(),
        tap((params) => {
          const filtersToAdd = params.filters.filter((item) => {
            return item.field !== 'serialNumber' && item.field !== 'range'
          })
          const filters = [
            new TermFilter('serialNumber', params.serialNumber),
            new RangeFilter(0, timestamp),
            ...filtersToAdd,
          ]
          this.dataService
            .retrieveData(
              this.dataService.createQuery(
                {count: 1, filters: filters, query: params.query, offset: 0},
                this.listConfig.eventName,
              ),
              [{field: 'ingestSequence', asc: false}],
            )
            .pipe(
              first(),
              map((res) => res.items),
              tap((rows) => {
                this.jumpToRow(rows[0], params)
              }),
            )
            .subscribe()
        }),
      )
      .subscribe()
  }

  jumpToNextOrPreviousRow(
    sourceName,
    direction,
    baseIngestSequence,
    params,
    mustOffset = true,
  ): Observable<number | null> {
    // no baseIngestSequence is early returning, prevent api-calls
    if (!baseIngestSequence) {
      return of(null)
    }
    // start with time backward and overwrite for time forward
    let asc = false
    let rangeQuery = `+ingestSequence:[0 TO ${baseIngestSequence}]`

    if (direction === loadDirection.TIME_FORWARD) {
      asc = true
      rangeQuery = ` +ingestSequence:[${baseIngestSequence} TO *]`
    }
    // now get the ingestSequence to jump to (sourcname, serialnumber, range and sorting order with offset 1)
    // this returns the next, previous or empty array
    const filters = [new TermFilter('serialNumber', params.serialNumber)]
    if (sourceName) {
      filters.push(
        new TermFilter(this.listConfig.convenienceFilter, sourceName),
      )
    }
    this.dataService
      .retrieveData(
        this.dataService.createQuery(
          {
            count: 1,
            filters: filters,
            query: rangeQuery + ' ' + params.query,
            offset: mustOffset ? 1 : 0,
          },
          this.listConfig.eventName,
        ),
        [{field: 'ingestSequence', asc: asc}],
      )
      .pipe(
        first(),
        map((res) => res.items),
        map((res) => {
          if (res.length) {
            return res[0]
          }
          return null
        }),
        tap((row) => {
          if (!row) {
            return this.setState({
              queryParamsHandling: 'merge',
              queryParams: {baseISN: null, startISN: null},
              row: null,
            })
          }
          this.jumpToRow(row, params)
        }),
      )
      .subscribe()
  }

  jumpToRow(row, params, merge = true, replace = false) {
    if (!row) {
      return
    }
    // set count to 5, percentage does not work very nice because the amount of rows varies.
    const count = this.selectedOffset
    this.lastOffset = count
    const rangeQuery = ` +ingestSequence:[${row['ingestSequence']} TO *]`
    const filters = params.filters
    // always ascending because it appends some newer results
    // !!! If more than 1 filter for sourceName, it becomes a query !!!
    this.dataService
      .retrieveData(
        this.dataService.createQuery(
          {
            count: count,
            filters: filters,
            query: rangeQuery + ' ' + params.query,
            offset: 1,
          },
          this.listConfig.eventName,
        ),
        [{field: 'ingestSequence', asc: true}],
      )
      .pipe(
        first(),
        map((res) => res.items),
        tap((result) => {
          // the ingestSequence number to start with is the first in this result
          let startISN = row.ingestSequence
          if (result.length) {
            startISN = result[0].ingestSequence
          }
          let mergetype = null
          if (merge) {
            mergetype = 'merge'
          }
          return this.setState({
            queryParamsHandling: mergetype,
            queryParams: {startISN: startISN, baseISN: row['ingestSequence']},
            row: row,
            replace: replace,
          })
        }),
      )
      .subscribe()
  }

  selectRow(item: TerminalEvent, detailViewModal: TemplateRef<any>) {
    // this selects a row, so no redirect/new query
    if (
      detailViewModal !== undefined &&
      this.listConfig.filterBlockConfig.dynamicFilter
    ) {
      this.open(detailViewModal)
    }
    return this.setState({
      queryParamsHandling: 'merge',
      queryParams: {baseISN: item.ingestSequence, startISN: null},
      row: item,
    })
  }

  resetView() {
    this.onResetView.emit(true)
  }

  getData({
    start,
    end,
    filters,
    query = '',
    ingestSequence,
  }: DataFetchInterface) {
    const ts = [moment().subtract(1, 'day').valueOf(), moment().valueOf()]
    const minTs = ts[0]
    const maxTs = ts[1]

    const _filters = []
    let hasDateFilter = false
    filters.forEach((f) => {
      // add
      _filters.push(f)
      if (f.type === 'range') {
        hasDateFilter = true
      }
    })

    if (!hasDateFilter && !ingestSequence) {
      _filters.push(new RangeFilter(minTs, maxTs))
    }

    this.fetching$.next({fetching: true, direction: this.direction})
    return this.dataService
      .getData(start, end, _filters, query, ingestSequence, this.listConfig)
      .pipe(
        finalize(() => {
          this.fetching$.next({fetching: false, direction: this.direction})
          this.changeDetectorRef.detectChanges()
        }),
      )
  }

  exportData() {
    if (this.searchParamsSubscription) {
      this.searchParamsSubscription.unsubscribe()
    }

    this.searchParamsSubscription = this.searchParams$
      .pipe(
        take(1),
        switchMap((params: SearchParams) => {
          let rangeFilter = params.filters.find(
            (filter) => filter.field === '@timestamp',
          ) as unknown as RangeFilter
          if (!rangeFilter) {
            const ts = [
              moment().subtract(1, 'day').valueOf(),
              moment().valueOf(),
            ]
            const minTs = ts[0]
            const maxTs = ts[1]
            rangeFilter = new RangeFilter(minTs, maxTs)
          }

          const start = rangeFilter.from
          const end = rangeFilter.to
          const query = params.query
          const filters: any = params.filters
          filters.push(rangeFilter)

          return this.dataService.getAllData(
            start,
            end,
            filters,
            query,
            null,
            this.listConfig,
          )
        }),
      )
      .subscribe(
        (data) => {
          const report = new TransactionReport(data.items)
          report
            .generate()
            .then((blob: Blob) => {
              const ts = Math.floor(Date.now() / 1000)
              saveAs(
                blob,
                `${this.serialNumberAsSubj$.value}_${ts}_events.xlsx`,
              )
            })
            .catch((error) => {
              console.error('Error generating blob:', error)
            })
        },
        (error) => {
          console.error('Error fetching data:', error)
        },
      )
  }
}
